import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { downloadFile } from '../utils/capacitor';
import {
  Upload, FileArchive, Image, Loader2,
  CheckCircle, Download, AlertCircle, XCircle, RefreshCw, X, Target, Zap, Clock, Flame
} from 'lucide-react';
import ToolSeoHead from '../components/seo/ToolSeoHead';
import ToolLandingSections from '../components/seo/ToolLandingSections';
import PerfectForSection from '../components/seo/PerfectForSection';
import ToolPageLabel from '../components/seo/ToolPageLabel';
import { PublicToolHeader } from '../components/PublicToolHeader';
import { getToolLandingConfig } from '../lib/toolLandingConfigs';
import { publicApi } from '../lib/publicApi';

const SERVICE_META = {
  'pdf-compression': {
    name: 'PDF Compression',
    description: 'Upload a PDF to reduce its file size while maintaining document clarity.',
    icon: FileArchive,
    credits: 2,
    accept: '.pdf',
    maxSizeMB: 50,
    apiPath: '/services/pdf-compression',
  },
  'visa-passport-photo': {
    name: 'Visa & Passport Photo',
    description: 'Upload a portrait photo and get a compliant visa or passport photo, ready for digital use or print.',
    icon: Image,
    credits: 5,
    accept: 'image/*',
    maxSizeMB: 20,
    apiPath: '/services/visa-photo',
  },
  'visa-photo': {
    name: 'Visa & Passport Photo',
    description: 'Upload a portrait photo and get a compliant visa or passport photo, ready for digital use or print.',
    icon: Image,
    credits: 5,
    accept: 'image/*',
    maxSizeMB: 20,
    apiPath: '/services/visa-photo',
  },
};

const COMPRESSION_MODES = [
  { value: 'standard', label: 'Standard', icon: Zap, desc: 'Best balance of size and quality.' },
  { value: 'aggressive', label: 'Aggressive', icon: Flame, desc: 'Maximum reduction. Lower image quality.' },
  { value: 'target_size', label: 'Target Size', icon: Target, desc: 'Compress to a specific file size.' },
];

const TARGET_PRESETS = [
  { value: '2', label: 'Visa Upload \u2014 Under 2 MB' },
  { value: '5', label: 'Email Attachment \u2014 Under 5 MB' },
  { value: '10', label: 'General \u2014 Under 10 MB' },
  { value: 'custom', label: 'Custom target size', desc: 'Compress to the selected maximum file size.' },
];

const DEFAULT_CONFIG_STATUS = {
  storage: { configured: true },
  pdf_compression: { configured: true },
  visa_photo: { configured: true },
};

function formatBytes(bytes) {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export default function ServicePage() {
  const { serviceType } = useParams();
  const [searchParams] = useSearchParams();
  const { user, api, refreshUser } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const rerunJobId = searchParams.get('rerunJobId');

  const [configStatus, setConfigStatus] = useState(DEFAULT_CONFIG_STATUS);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStats, setUploadStats] = useState({ loaded: 0, total: 0, speed: 0, eta: 0 });
  const [jobResult, setJobResult] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // granular: queued, compressing, finalizing
  const [outputPreviewUrl, setOutputPreviewUrl] = useState(null);
  const [print4rPreviewUrl, setPrint4rPreviewUrl] = useState(null);
  const [polling, setPolling] = useState(false);
  const [rerunInfo, setRerunInfo] = useState(null);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const uploadEndTimeRef = useRef(null);
  const uploadSpeedSamples = useRef([]);
  const [completionTime, setCompletionTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [uploadPhase, setUploadPhase] = useState(null); // 'uploading' | 'processing' | null
  // Short-lived server-side raw PDF cache (upload_id) for mode retries without re-upload.
  const [uploadId, setUploadId] = useState(null);
  const [uploadExpiresAt, setUploadExpiresAt] = useState(null);

  // PDF compression mode state
  const [compressMode, setCompressMode] = useState('standard');
  const [targetPreset, setTargetPreset] = useState('2');
  const [customTarget, setCustomTarget] = useState('');

  // Visa photo output format
  const [photoOutputFormat, setPhotoOutputFormat] = useState('digital');

  const config = SERVICE_META[serviceType];
  const seoConfig = getToolLandingConfig(serviceType);
  const isVisaPhoto = serviceType === 'visa-passport-photo' || serviceType === 'visa-photo';
  const isPdfCompression = serviceType === 'pdf-compression';

  const requireAuth = () => {
    if (user) return true;
    const message = seoConfig?.loginGateMessage || 'Please sign in to continue';
    toast.info(message);
    navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`);
    return false;
  };

  // Live elapsed timer while uploading or processing
  useEffect(() => {
    if (!uploadStartTime || completionTime) return;
    const interval = setInterval(() => {
      setElapsedTime(Math.round((Date.now() - uploadStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [uploadStartTime, completionTime]);

  useEffect(() => {
    fetchConfig();
    if (rerunJobId && user) loadRerunData();
    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
      if (outputPreviewUrl) URL.revokeObjectURL(outputPreviewUrl);
      if (print4rPreviewUrl) URL.revokeObjectURL(print4rPreviewUrl);
    };
  }, [serviceType, user, rerunJobId]);

  const loadRerunData = async () => {
    try {
      const res = await api.get(`/jobs/${rerunJobId}/rerun-data`);
      const d = res.data;
      setRerunInfo(d);
      if (d.service_type === 'pdf_compression' && d.settings) {
        setCompressMode(d.settings.mode || 'standard');
        if (d.settings.mode === 'target_size') {
          const mb = String(d.settings.target_size_mb);
          const isPreset = TARGET_PRESETS.some(p => p.value === mb);
          setTargetPreset(isPreset ? mb : 'custom');
          if (!isPreset) setCustomTarget(mb);
        }
      }
      if (d.service_type === 'visa_photo' && d.settings?.preset) {
        setSelectedPreset(d.settings.preset);
      }
      toast.info('Previous settings loaded. Please upload your file to continue.');
    } catch { toast.error('Failed to load re-run data'); }
  };

  const fetchConfig = async () => {
    try {
      const res = await publicApi.get('/services/config-status');
      setConfigStatus(res.data);
    } catch {
      setConfigStatus(DEFAULT_CONFIG_STATUS);
    }

    if (isVisaPhoto) {
      try {
        const presetsRes = await publicApi.get('/services/visa-photo/presets');
        setPresets(presetsRes.data.presets || []);
        if (presetsRes.data.presets?.length > 0) setSelectedPreset(presetsRes.data.presets[0].id);
      } catch {
        setPresets([]);
      }
    }
  };

  const isServiceConfigured = () => {
    if (!configStatus) return false;
    if (isPdfCompression) return configStatus.pdf_compression?.configured;
    if (isVisaPhoto) return configStatus.visa_photo?.configured;
    return false;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > config.maxSizeMB * 1024 * 1024) {
      toast.error(`File too large. Maximum ${config.maxSizeMB}MB.`);
      return;
    }
    setSelectedFile(file);
    setUploadId(null);
    setUploadExpiresAt(null);
    setJobResult(null);
    setOutputPreviewUrl(null);
    setPrint4rPreviewUrl(null);
    if (isVisaPhoto && file.type.startsWith('image/')) {
      if (filePreview) URL.revokeObjectURL(filePreview);
      setFilePreview(URL.createObjectURL(file));
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadId(null);
    setUploadExpiresAt(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    setJobResult(null);
    setOutputPreviewUrl(null);
    setPrint4rPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getTargetMb = () => {
    if (targetPreset === 'custom') return parseFloat(customTarget) || 2;
    return parseFloat(targetPreset);
  };

  const isUploadCacheValid = () => {
    if (!uploadId || !uploadExpiresAt) return false;
    const expiresMs = Date.parse(uploadExpiresAt);
    if (Number.isNaN(expiresMs)) return false;
    return Date.now() < expiresMs - 5000; // 5s skew
  };

  const handleUpload = async () => {
    if (!requireAuth()) return;
    if (!selectedFile) return toast.error('Please select a file first');
    if (isVisaPhoto && !selectedPreset) return toast.error('Please select a photo preset');

    // Upfront credit check
    const creditBalance = user?.credit_balance || 0;
    if (creditBalance < config.credits) {
      toast.error(`Insufficient credits. You need ${config.credits} credits but have ${creditBalance}. Please add credits first.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStats({ loaded: 0, total: selectedFile.size, speed: 0, eta: 0 });
    setUploadPhase('uploading');
    setJobResult(null);
    setJobStatus(null);
    setOutputPreviewUrl(null);
    setPrint4rPreviewUrl(null);
    setCompletionTime(null);
    setElapsedTime(0);
    const startTime = Date.now();
    setUploadStartTime(startTime);
    uploadEndTimeRef.current = null;
    uploadSpeedSamples.current = [];

    const MAX_RETRIES = 2;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          toast.info(`Retrying upload (attempt ${attempt + 1}/${MAX_RETRIES + 1})...`);
          setUploadProgress(0);
          setUploadStats(s => ({ ...s, loaded: 0, speed: 0, eta: 0 }));
          setUploadPhase('uploading');
        }

        // Visa photo: legacy single multipart POST (unchanged).
        if (isVisaPhoto) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          const url = `${config.apiPath}?preset=${selectedPreset}&output_format=${photoOutputFormat}`;
          const res = await api.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 600000,
            onUploadProgress: (ev) => {
              if (!ev.total) return;
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setUploadProgress(pct);
              const now = Date.now();
              const elapsedMs = now - startTime;
              const speedBps = elapsedMs > 500 ? ev.loaded / (elapsedMs / 1000) : 0;
              uploadSpeedSamples.current.push(speedBps);
              if (uploadSpeedSamples.current.length > 5) uploadSpeedSamples.current.shift();
              const avgSpeed = uploadSpeedSamples.current.reduce((a, b) => a + b, 0) / uploadSpeedSamples.current.length;
              const remaining = ev.total - ev.loaded;
              const etaSec = avgSpeed > 0 ? Math.round(remaining / avgSpeed) : 0;
              setUploadStats({ loaded: ev.loaded, total: ev.total, speed: avgSpeed, eta: etaSec });
            },
          });
          uploadEndTimeRef.current = Date.now();
          setUploadProgress(100);
          setUploadPhase('processing');
          toast.success('Upload complete — processing started.');
          pollJobStatus(res.data.job_id, startTime);
          return;
        }

        // PDF compression: upload once → upload_id → compress-by-id (credits on compress only).
        let activeUploadId = isUploadCacheValid() ? uploadId : null;

        if (!activeUploadId) {
          const uploadFd = new FormData();
          uploadFd.append('file', selectedFile);
          const upRes = await api.post('/services/pdf-upload', uploadFd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 600000,
            onUploadProgress: (ev) => {
              if (!ev.total) return;
              const pct = Math.round((ev.loaded / ev.total) * 100);
              setUploadProgress(pct);
              const now = Date.now();
              const elapsedMs = now - startTime;
              const speedBps = elapsedMs > 500 ? ev.loaded / (elapsedMs / 1000) : 0;
              uploadSpeedSamples.current.push(speedBps);
              if (uploadSpeedSamples.current.length > 5) uploadSpeedSamples.current.shift();
              const avgSpeed = uploadSpeedSamples.current.reduce((a, b) => a + b, 0) / uploadSpeedSamples.current.length;
              const remaining = ev.total - ev.loaded;
              const etaSec = avgSpeed > 0 ? Math.round(remaining / avgSpeed) : 0;
              setUploadStats({ loaded: ev.loaded, total: ev.total, speed: avgSpeed, eta: etaSec });
            },
          });
          activeUploadId = upRes.data.upload_id;
          setUploadId(activeUploadId);
          setUploadExpiresAt(upRes.data.expires_at);
          setUploadProgress(100);
        } else {
          setUploadProgress(100);
          setUploadStats(s => ({ ...s, loaded: selectedFile.size, total: selectedFile.size, speed: 0, eta: 0 }));
        }

        uploadEndTimeRef.current = Date.now();
        setUploadPhase('processing');

        const compressFd = new FormData();
        compressFd.append('upload_id', activeUploadId);
        compressFd.append('mode', compressMode);
        if (compressMode === 'target_size') {
          compressFd.append('target_size_mb', getTargetMb());
        }

        const res = await api.post(config.apiPath, compressFd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });

        toast.success('Upload complete — processing started.');
        pollJobStatus(res.data.job_id, startTime);
        return;
      } catch (error) {
        const status = error.response?.status;
        // Cached upload expired — drop cache and retry (counts as an attempt).
        if (status === 410 && isPdfCompression) {
          setUploadId(null);
          setUploadExpiresAt(null);
          if (attempt < MAX_RETRIES) continue;
        }

        const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED';
        if (isNetworkError && attempt < MAX_RETRIES) {
          continue;
        }
        const detail = error.response?.data?.detail || 'Upload failed';
        if (error.response?.status === 400 && detail.includes('Insufficient credits')) {
          toast.error('Insufficient credits. Please add credits first.');
        } else if (isNetworkError) {
          toast.error('Upload failed after multiple attempts. Please check your connection and try again.');
        } else {
          toast.error(detail);
        }
        setUploading(false);
        setUploadStartTime(null);
        setUploadPhase(null);
      }
    }
  };

  const pollJobStatus = async (jobId, startTime) => {
    // Must exceed backend PDF compression OVERALL_TIME_BUDGET_SECONDS (300s)
    // plus upload/finalize margin (~26s observed). Keep a finite ceiling for
    // genuine connectivity failures — not infinite polling.
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_MS = 360_000; // 360s — matches ~300s backend budget + margin
    const deadline = Date.now() + MAX_POLL_MS;

    setPolling(true);
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      try {
        const res = await api.get(`/jobs/${jobId}`);
        const job = res.data;
        // Update granular status for UI
        if (job.status && job.status !== 'completed' && job.status !== 'failed') {
          setJobStatus(job.status);
        }
        if (job.status === 'completed') {
          // Includes method=best_effort / target_met=false — valid completion,
          // not an error; result card shows Target Not Met when applicable.
          const doneTime = Date.now();
          setCompletionTime(doneTime);
          // Use ref for uploadEndTime to avoid stale closure
          const uploadEnd = uploadEndTimeRef.current || doneTime;
          const clientTiming = {
            upload_time_s: Math.round((uploadEnd - startTime) / 1000),
            total_time_s: Math.round((doneTime - startTime) / 1000),
          };
          setJobResult({ ...job, canDownload: true, clientTiming });
          setJobStatus(null);
          setPolling(false);
          setUploading(false);
          setUploadPhase(null);
          toast.success('Processing complete!');
          setTimeout(() => refreshUser(), 500);
          if (isVisaPhoto) {
              fetchOutputPreview(job.id);
              if (job.metadata?.print_4r_preview_ref) fetch4rPreview(job.id);
            }
          return;
        } else if (job.status === 'failed') {
          setCompletionTime(Date.now());
          setJobResult({ ...job, canDownload: false });
          setJobStatus(null);
          setPolling(false);
          setUploading(false);
          setUploadPhase(null);
          toast.error(job.error_message || 'Processing failed');
          return;
        }
      } catch { /* keep polling */ }
    }
    setPolling(false);
    setUploading(false);
    setUploadPhase(null);
    setCompletionTime(Date.now());
    toast.error('Processing timed out. Check job history.');
  };

  const fetchOutputPreview = async (jobId) => {
    try {
      const res = await api.get(`/storage/download/${jobId}?variant=digital`, { responseType: 'blob' });
      setOutputPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' })));
    } catch { /* preview not critical */ }
  };

  const fetch4rPreview = async (jobId) => {
    try {
      const res = await api.get(`/storage/download/${jobId}?variant=print_4r_preview`, { responseType: 'blob' });
      setPrint4rPreviewUrl(URL.createObjectURL(new Blob([res.data], { type: 'image/jpeg' })));
    } catch { /* preview not critical */ }
  };

  const handleDownload = async (variant = 'default') => {
    if (!requireAuth()) return;
    if (!jobResult?.id) return;
    try {
      toast.info('Preparing download...');
      const res = await api.get(`/storage/download/${jobResult.id}?variant=${variant}`, { responseType: 'blob' });
      let filename;
      if (isVisaPhoto) {
        filename = variant === 'print_4r' ? `visa_photo_4R_${jobResult.id.slice(0,8)}.jpg` : `visa_photo_${jobResult.id.slice(0,8)}.jpg`;
      } else {
        filename = `compressed_${selectedFile?.name || 'output.pdf'}`;
      }
      const result = await downloadFile(new Blob([res.data]), filename);
      if (result.method === 'native') {
        toast.success(`Saved to Documents: ${filename}`);
      } else {
        toast.success('Download started');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Download failed. The file may no longer be available.';
      toast.error(detail);
    }
  };

  // Helper: format seconds as human-readable duration
  const _formatDuration = (totalSeconds) => {
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}m ${String(secs).padStart(2, '0')}s`;
  };

  // Reusable timing breakdown renderer
  const renderTimingBreakdown = (result) => {
    if (!result) return null;
    const ct = result.clientTiming || {};
    const bt = result.timing || {};
    const totalSec = ct.total_time_s || 0;
    const uploadSec = ct.upload_time_s || 0;
    const fileReadSec = bt.file_read_time_s || 0;
    const queueSec = bt.queue_wait_s || 0;
    const compressSec = bt.compression_time_s || bt.processing_time_s || result.metadata?.processing_time_s || 0;
    const finalizeSec = bt.finalize_time_s || 0;

    const serverProcessingSec = Math.round(fileReadSec + queueSec + compressSec + finalizeSec);
    const networkSec = Math.max(0, uploadSec - Math.round(fileReadSec));
    const uploadDominates = networkSec > 10 && totalSec > 0 && networkSec > serverProcessingSec;

    if (!totalSec && !compressSec) return null;

    return (
      <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2" data-testid="timing-breakdown">
        {totalSec > 0 && (
          <div className="flex items-center justify-between pb-1 border-b border-border/30">
            <span className="text-sm font-medium text-foreground flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" /> Total elapsed
            </span>
            <span className="text-sm font-semibold text-foreground" data-testid="total-time">{_formatDuration(totalSec)}</span>
          </div>
        )}
        <div className="space-y-1">
          {networkSec > 2 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Upload (your network)</span>
              <span data-testid="upload-time">{_formatDuration(networkSec)}</span>
            </div>
          )}
          {fileReadSec >= 0.5 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Server file read</span>
              <span data-testid="file-read-time">{fileReadSec.toFixed(1)}s</span>
            </div>
          )}
          {queueSec > 0 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Queue wait</span>
              <span data-testid="queue-time">{_formatDuration(Math.round(queueSec))}</span>
            </div>
          )}
          {compressSec > 0 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{isPdfCompression ? 'Compression' : 'Processing'}</span>
              <span data-testid="compression-time">{_formatDuration(Math.round(compressSec))}</span>
            </div>
          )}
          {finalizeSec > 0 && (
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Storage upload</span>
              <span data-testid="finalize-time">{_formatDuration(Math.round(finalizeSec))}</span>
            </div>
          )}
          {serverProcessingSec > 0 && totalSec > 0 && (
            <div className="flex items-center justify-between text-xs font-medium text-foreground pt-1 border-t border-border/20">
              <span>Server processing</span>
              <span data-testid="server-processing-time">{_formatDuration(serverProcessingSec)}</span>
            </div>
          )}
        </div>
        {uploadDominates && (
          <p className="text-xs text-muted-foreground/80 pt-1.5 border-t border-border/30 leading-relaxed" data-testid="upload-dominant-note">
            Most of the elapsed time was spent uploading your file over the network. Server processed it in just <strong className="text-foreground">{_formatDuration(serverProcessingSec)}</strong>.
          </p>
        )}
      </div>
    );
  };

  // Helper: get human-readable label for granular job status
  const _getStatusLabel = (status) => {
    const labels = {
      queued: 'Queued',
      compressing: 'Compressing',
      finalizing: 'Finalizing',
      processing: 'Processing',
    };
    return labels[status] || status;
  };

  // Helper: get button label based on current state
  const _getProcessingLabel = () => {
    if (uploadPhase === 'uploading') {
      if (isVisaPhoto) return 'Uploading photo...';
      return uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Queuing...';
    }
    if (uploadPhase === 'processing' || polling) {
      if (jobStatus === 'queued') return 'Queued, waiting...';
      if (jobStatus === 'compressing') return isVisaPhoto ? 'Processing photo...' : 'Compressing...';
      if (jobStatus === 'finalizing') return 'Saving result...';
      return isVisaPhoto ? 'Processing...' : 'Compressing...';
    }
    return 'Processing...';
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="card-elevated max-w-md w-full">
          <CardContent className="text-center py-12">
            <p className="text-lg font-semibold text-foreground mb-2">Service Not Found</p>
            <Button variant="outline" onClick={() => navigate('/')}>Back to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const IconComponent = config.icon;
  const configured = isServiceConfigured();
  const meta = jobResult?.metadata;

  return (
    <div className="min-h-screen bg-muted/20">
      {seoConfig && (
        <ToolSeoHead
          title={seoConfig.titleTag}
          description={seoConfig.metaDescription}
          canonicalPath={seoConfig.canonicalPath}
        />
      )}
      <PublicToolHeader backTo="/" />

      <main className="max-w-3xl mx-auto p-6 lg:p-10 space-y-6">
        <div className="space-y-3">
          {seoConfig?.pageLabel && (
            <ToolPageLabel
              label={seoConfig.pageLabel}
              exploreAction={isPdfCompression ? 'compress' : 'generate'}
            />
          )}
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-testid="service-page-title">
            {seoConfig?.heroHeadline || config.name}
          </h1>
          <p className="text-muted-foreground">
            {seoConfig?.heroSubtext || config.description}
          </p>
          {seoConfig?.keywords && <p className="sr-only">{seoConfig.keywords.join(', ')}</p>}
        </div>

        {/* Not Configured Warning */}
        {user && configStatus && !configured && (
          <Card className="border-destructive/20 bg-destructive/5" data-testid="service-not-configured">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
              <div>
                <p className="font-medium text-foreground">Service Not Configured</p>
                <p className="text-sm text-muted-foreground">This service requires API keys to be configured by the administrator.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Visa Photo Preset Selector */}
        {isVisaPhoto && presets.length > 0 && (
          <Card className="card-elevated" data-testid="preset-card">
            <CardHeader>
              <CardTitle className="text-base">Photo Specification</CardTitle>
              <CardDescription>Choose the country and document type for your photo</CardDescription>
            </CardHeader>
            <CardContent>
              <Select value={selectedPreset} onValueChange={setSelectedPreset}>
                <SelectTrigger data-testid="preset-select"><SelectValue placeholder="Select specification" /></SelectTrigger>
                <SelectContent>
                  {presets.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.preset_name} ({p.country_code}) &mdash; {p.width_mm}x{p.height_mm}mm
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* PDF Compression Mode Selector */}
        {isPdfCompression && (
          <Card className="card-elevated" data-testid="compression-mode-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Compression Mode</CardTitle>
              <CardDescription>Choose how you want to compress your PDF</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {COMPRESSION_MODES.map(m => {
                  const MIcon = m.icon;
                  const active = compressMode === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setCompressMode(m.value)}
                      disabled={uploading}
                      data-testid={`mode-${m.value}`}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        active
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border hover:border-primary/30 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <MIcon className={`h-4 w-4 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                        <span className={`font-medium text-sm ${active ? 'text-primary' : 'text-foreground'}`}>{m.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{m.desc}</p>
                    </button>
                  );
                })}
              </div>

              {/* Aggressive Mode Warning */}
              {compressMode === 'aggressive' && (
                <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3 text-sm text-orange-800" data-testid="aggressive-warning">
                  <p className="font-medium">Quality trade-off</p>
                  <p className="text-xs text-orange-600 mt-1">Images will be noticeably lower quality. Text remains legible. Use when file size is more important than visual quality.</p>
                </div>
              )}

              {/* Target Size Options */}
              {compressMode === 'target_size' && (
                <div className="space-y-3 pt-2" data-testid="target-options">
                  <label className="text-sm font-medium text-foreground">Target Size</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {TARGET_PRESETS.map(p => {
                      const active = targetPreset === p.value;
                      return (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => setTargetPreset(p.value)}
                          disabled={uploading}
                          data-testid={`target-${p.value}`}
                          className={`text-left px-4 py-3 rounded-lg border transition-all ${
                            active
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/30 bg-white'
                          }`}
                        >
                          <span className={`text-sm font-medium ${active ? 'text-primary' : 'text-foreground'}`}>{p.label}</span>
                          {p.desc && <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>}
                        </button>
                      );
                    })}
                  </div>
                  {targetPreset === 'custom' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0.1" max="50" step="0.1"
                        value={customTarget}
                        onChange={e => setCustomTarget(e.target.value)}
                        placeholder="e.g. 3"
                        className="flex h-10 w-32 rounded-md border border-border bg-background px-3 py-2 text-sm"
                        data-testid="custom-target-input"
                      />
                      <span className="text-sm text-muted-foreground">MB</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Logged-out: sign-in prompt */}
        {!user && !jobResult && (
          <Card className="card-elevated border-primary/30 bg-primary/5" data-testid="service-sign-in-prompt">
            <CardContent className="py-6 space-y-4 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
                <IconComponent className="h-7 w-7 text-primary" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-foreground">{config.name}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {isVisaPhoto
                    ? 'Generate visa-compliant photos accepted for official applications.'
                    : 'Reduce your file size by up to 80% — fast and secure.'}
                </p>
              </div>
              <Button
                className="glow-button px-8"
                data-testid="service-sign-in-btn"
                onClick={() => requireAuth()}
              >
                Sign in to continue
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Logged-in with insufficient credits */}
        {user && (user.credit_balance || 0) < config.credits && !jobResult && (
          <Card className="card-elevated border-primary/30 bg-primary/5" data-testid="service-credit-teaser">
            <CardContent className="py-6 space-y-4 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
                <IconComponent className="h-7 w-7 text-primary" />
              </div>
              <div className="space-y-1">
                <h3 className="text-xl font-bold text-foreground">{config.name}</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  {isVisaPhoto
                    ? 'Generate visa-compliant photos accepted for official applications.'
                    : 'Reduce your file size by up to 80% — fast and secure.'}
                </p>
              </div>
              <p className="text-sm text-foreground font-medium">
                Unlock to continue.
              </p>
              <div className="flex flex-col items-center gap-2">
                <Button
                  className="glow-button px-8"
                  data-testid="service-teaser-continue"
                  onClick={() => navigate('/credits')}
                >
                  Continue — {config.credits} credits
                </Button>
                <p className="text-xs text-muted-foreground">
                  Your balance: {user.credit_balance || 0} credits. You need {config.credits - (user.credit_balance || 0)} more.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload & Preview Section (logged-in users with sufficient credits) */}
        {user && (user.credit_balance || 0) >= config.credits && (
        <Card className="card-elevated" data-testid="service-upload-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              {isVisaPhoto ? 'Step 1 \u2014 Upload Your Photo' : 'Upload File'}
            </CardTitle>
            <CardDescription>
              {isVisaPhoto
                ? 'Select a clear portrait photo. Your photo will be processed automatically.'
                : 'Select a PDF file to compress (max 50MB)'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File preview (visa photo) */}
            {isVisaPhoto && filePreview && (
              <div className="flex flex-col sm:flex-row items-start gap-4" data-testid="upload-preview-section">
                <div className="relative w-40 h-52 rounded-lg border overflow-hidden bg-white shrink-0">
                  <img src={filePreview} alt="Upload preview" className="w-full h-full object-cover" data-testid="upload-preview-img" />
                </div>
                <div className="space-y-2 py-2">
                  <p className="font-medium text-foreground text-sm">{selectedFile?.name}</p>
                  <p className="text-xs text-muted-foreground">{(selectedFile?.size / 1024 / 1024).toFixed(2)} MB</p>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <Button type="button" variant="outline" size="sm" asChild>
                        <span><RefreshCw className="h-3 w-3 mr-1" /> Replace</span>
                      </Button>
                      <input type="file" accept={config.accept} className="hidden" onChange={handleFileSelect} disabled={uploading} />
                    </label>
                    <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={removeFile} data-testid="remove-file-btn">
                      <X className="h-3 w-3 mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Non-image file selected (PDF) */}
            {!isVisaPhoto && selectedFile && (
              <div className="flex items-center gap-4 p-4 rounded-lg border bg-muted/20" data-testid="upload-preview-section">
                <FileArchive className="h-8 w-8 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-foreground text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span><RefreshCw className="h-3 w-3 mr-1" /> Replace</span>
                    </Button>
                    <input type="file" accept={config.accept} className="hidden" onChange={handleFileSelect} disabled={uploading} />
                  </label>
                  <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={removeFile} data-testid="remove-file-btn">
                    <X className="h-3 w-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            )}

            {/* Large file advisory */}
            {selectedFile && selectedFile.size > 10 * 1024 * 1024 && !uploading && !jobResult && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200/60 text-sm text-amber-800 dark:bg-amber-950/20 dark:border-amber-800/30 dark:text-amber-200" data-testid="large-file-warning">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Large file ({(selectedFile.size / 1048576).toFixed(0)} MB)</p>
                  <p className="text-xs mt-0.5 opacity-80">Upload time depends on your internet speed. Processing on our servers typically takes under 10 seconds.</p>
                </div>
              </div>
            )}

            {/* File Input (dropzone) */}
            {!selectedFile && (
              <div
                className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/30 transition-colors cursor-pointer"
                onClick={() => !uploading && fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input ref={fileInputRef} type="file" accept={config.accept} className="hidden" onChange={handleFileSelect} disabled={uploading} data-testid="file-input" />
                <div className="space-y-2">
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground/50" />
                  <p className="font-medium text-muted-foreground">Click to select a file</p>
                  <p className="text-xs text-muted-foreground">{isVisaPhoto ? 'Image files up to 20MB' : 'PDF files up to 50MB'}</p>
                </div>
              </div>
            )}

            {/* Visa Photo — Output Format Selector */}
            {isVisaPhoto && selectedFile && (
              <div className="space-y-2" data-testid="output-format-section">
                <label className="text-sm font-medium text-foreground">Output Format</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'digital', label: 'Digital Photo', desc: 'Single passport photo' },
                    { value: 'print_4r', label: '4R Print Sheet', desc: 'Multiple copies on 4\u00D76 inch sheet' },
                    { value: 'both', label: 'Both', desc: 'Digital + print sheet' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setPhotoOutputFormat(opt.value)}
                      className={`p-3 rounded-lg border text-left transition-all text-sm ${photoOutputFormat === opt.value ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:border-primary/30'}`}
                      data-testid={`output-format-${opt.value}`}
                    >
                      <p className="font-medium text-foreground">{opt.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Process Button */}
            <Button
              className="w-full glow-button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading || !configured || (user?.credit_balance || 0) < config.credits}
              data-testid="process-btn"
            >
              {uploading || polling ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{_getProcessingLabel()}</>
              ) : (user?.credit_balance || 0) < config.credits ? (
                'Insufficient Credits'
              ) : (
                <><IconComponent className="mr-2 h-4 w-4" />{isVisaPhoto ? 'Generate Photo' : 'Compress PDF'} &mdash; {config.credits} credits</>
              )}
            </Button>

            {/* Upload progress bar with speed & ETA */}
            {uploading && uploadPhase === 'uploading' && (
              <div className="space-y-2 p-3 rounded-lg bg-muted/30 border border-border/50" data-testid="upload-progress-section">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-foreground font-medium flex items-center gap-1.5">
                    <Upload className="h-3.5 w-3.5 text-primary" />
                    Uploading to server
                  </span>
                  <span className="text-foreground font-medium">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span data-testid="upload-loaded">
                    {formatBytes(uploadStats.loaded)} of {formatBytes(uploadStats.total)}
                  </span>
                  <span className="flex items-center gap-2">
                    {uploadStats.speed > 0 && (
                      <span data-testid="upload-speed">{formatBytes(uploadStats.speed)}/s</span>
                    )}
                    {uploadStats.eta > 0 && uploadProgress < 99 && (
                      <span data-testid="upload-eta">~{_formatDuration(uploadStats.eta)} left</span>
                    )}
                  </span>
                </div>
                {selectedFile && selectedFile.size > 10 * 1024 * 1024 && uploadStats.speed > 0 && uploadStats.speed < 500000 && (
                  <p className="text-xs text-muted-foreground/80 pt-1 border-t border-border/30" data-testid="slow-upload-note">
                    Large file upload speed depends on your internet connection.
                  </p>
                )}
              </div>
            )}

            {/* Server processing phase indicator */}
            {uploadPhase === 'processing' && (
              <div className="space-y-2 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid="processing-phase-section">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span>Server processing</span>
                  {jobStatus && (
                    <Badge variant="outline" className="text-xs" data-testid="live-status-badge">{_getStatusLabel(jobStatus)}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Upload complete. Your file is being processed — this typically takes under 10 seconds.
                </p>
              </div>
            )}

            {/* Live elapsed timer */}
            {(uploading || polling) && uploadStartTime && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="elapsed-timer">
                <Clock className="h-3.5 w-3.5" />
                <span>Elapsed: {_formatDuration(elapsedTime)}</span>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Output Preview (Visa Photo) */}
        {isVisaPhoto && jobResult && jobResult.status === 'completed' && (
          <Card className="card-elevated border-primary/20" data-testid="output-preview-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                Step 2 &mdash; Generated Passport Photo
              </CardTitle>
              <CardDescription>
                {jobResult.metadata?.preset_name && (<span>{jobResult.metadata.preset_name} &mdash; {jobResult.metadata.dimensions_mm}</span>)}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                {filePreview && (
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Original</p>
                    <div className="w-36 h-48 rounded-lg border overflow-hidden bg-white">
                      <img src={filePreview} alt="Original" className="w-full h-full object-cover" />
                    </div>
                  </div>
                )}
                {outputPreviewUrl ? (
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Digital Photo</p>
                    <div className="w-36 h-48 rounded-lg border-2 border-primary/30 overflow-hidden bg-white shadow-md">
                      <img src={outputPreviewUrl} alt="Generated passport photo" className="w-full h-full object-cover" data-testid="output-preview-img" />
                    </div>
                  </div>
                ) : (
                  <div className="w-36 h-48 rounded-lg border flex items-center justify-center bg-muted/20">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                {print4rPreviewUrl && (
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">4R Print Sheet</p>
                    <div className="w-48 h-36 rounded-lg border-2 border-primary/30 overflow-hidden bg-white shadow-md">
                      <img src={print4rPreviewUrl} alt="4R print sheet preview" className="w-full h-full object-contain" data-testid="output-preview-4r-img" />
                    </div>
                  </div>
                )}
              </div>
              {jobResult.metadata && (
                <div className="flex flex-wrap gap-2">
                  {jobResult.metadata.file_size_kb && <Badge variant="outline">{jobResult.metadata.file_size_kb} KB</Badge>}
                  {jobResult.metadata.dimensions_mm && <Badge variant="outline">{jobResult.metadata.dimensions_mm}</Badge>}
                </div>
              )}
              {/* Download buttons — show two when "both" outputs exist */}
              {jobResult.metadata?.digital_ref && jobResult.metadata?.print_4r_ref ? (
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleDownload('digital')} className="glow-button" data-testid="download-digital-btn">
                    <Download className="mr-2 h-4 w-4" /> Digital Photo
                  </Button>
                  <Button onClick={() => handleDownload('print_4r')} variant="outline" className="border-primary text-primary hover:bg-primary/5" data-testid="download-4r-btn">
                    <Download className="mr-2 h-4 w-4" /> 4R Print Sheet
                  </Button>
                </div>
              ) : (
                <Button onClick={() => handleDownload()} className="w-full glow-button" data-testid="download-btn">
                  <Download className="mr-2 h-4 w-4" /> Download Passport Photo
                </Button>
              )}
              {renderTimingBreakdown(jobResult)}
            </CardContent>
          </Card>
        )}

        {/* PDF Compression Result Card */}
        {isPdfCompression && jobResult && jobResult.status === 'completed' && (() => {
          const isNoGain = meta && meta.compressed_size >= meta.original_size;
          const isTargetMode = meta?.mode === 'target_size';
          const isAggressive = meta?.mode === 'aggressive';
          const targetMet = meta?.target_met === true;
          const targetMb = meta?.target_mb;
          const hasQualityWarning = meta?.quality_warning;

          // Determine the mode label
          let modeLabel = 'Standard';
          if (isAggressive) modeLabel = 'Aggressive';
          else if (isTargetMode && targetMb) {
            if (targetMb == 2) modeLabel = `Visa Upload — Under ${targetMb} MB`;
            else modeLabel = `Target — Under ${targetMb} MB`;
          }

          return (
          <Card className={`card-elevated ${isNoGain ? 'border-amber-300/40' : isTargetMode && !targetMet ? 'border-amber-300/40' : 'border-primary/20'}`} data-testid="result-card">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start gap-4">
                {isNoGain ? (
                  <AlertCircle className="h-8 w-8 text-amber-500 shrink-0 mt-0.5" />
                ) : (
                  <CheckCircle className={`h-8 w-8 shrink-0 mt-0.5 ${targetMet || !isTargetMode ? 'text-primary' : 'text-amber-500'}`} />
                )}
                <div className="flex-1 space-y-3">
                  <p className="font-semibold text-foreground text-lg" data-testid="result-title">
                    {isNoGain ? 'Already Optimized' : isTargetMode && !targetMet ? 'Compressed (Target Not Met)' : 'Compression Complete'}
                  </p>

                  {/* Mode badge — always shows the ACTUAL selected mode */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" data-testid="badge-mode">{modeLabel}</Badge>
                    {isTargetMode && targetMet && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="badge-target-met">Target Met</Badge>}
                    {isTargetMode && !targetMet && !isNoGain && <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="badge-target-missed">Target Not Met</Badge>}
                    {hasQualityWarning && <Badge className="bg-orange-100 text-orange-700 border-orange-200" data-testid="badge-quality-warning">Reduced Quality</Badge>}
                  </div>

                  {/* Size comparison */}
                  {meta && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-lg bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Original</p>
                        <p className="font-semibold text-foreground text-sm" data-testid="original-size">{formatBytes(meta.original_size)}</p>
                      </div>
                      <div className={`p-3 rounded-lg text-center border ${isNoGain ? 'bg-muted/30 border-muted' : 'bg-primary/5 border-primary/10'}`}>
                        <p className="text-xs text-muted-foreground mb-1">{isNoGain ? 'Unchanged' : 'Compressed'}</p>
                        <p className={`font-semibold text-sm ${isNoGain ? 'text-muted-foreground' : 'text-primary'}`} data-testid="compressed-size">{formatBytes(meta.compressed_size)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-muted/30 text-center">
                        <p className="text-xs text-muted-foreground mb-1">Reduction</p>
                        <p className="font-semibold text-foreground text-sm" data-testid="reduction-pct">{meta.reduction_percent}%</p>
                      </div>
                    </div>
                  )}

                  {/* Message */}
                  {meta?.message && (
                    <p className={`text-sm rounded-lg px-3 py-2 ${isNoGain ? 'text-amber-700 bg-amber-50 border border-amber-200' : 'text-muted-foreground bg-muted/30'}`} data-testid="result-message">{meta.message}</p>
                  )}

                  {/* Suggest Aggressive mode when standard gives 0% */}
                  {isNoGain && meta?.mode === 'standard' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => { setCompressMode('aggressive'); setJobResult(null); }}
                      data-testid="try-aggressive-btn"
                    >
                      <Flame className="mr-1.5 h-3.5 w-3.5" /> Try Aggressive Mode
                    </Button>
                  )}

                  {/* Timing breakdown */}
                  {renderTimingBreakdown(jobResult)}
                </div>
              </div>

              {/* Download button — only if file was actually compressed */}
              {jobResult.canDownload && !isNoGain && (
                <Button onClick={() => handleDownload()} className="w-full glow-button" data-testid="download-btn">
                  <Download className="mr-2 h-4 w-4" /> Download Compressed PDF
                </Button>
              )}
            </CardContent>
          </Card>
          );
        })()}

        {/* Failed result */}
        {jobResult && jobResult.status === 'failed' && (
          <Card className="card-elevated border-destructive/20" data-testid="result-card-failed">
            <CardContent className="flex items-center gap-4 py-6">
              <XCircle className="h-8 w-8 text-destructive shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-foreground">Processing Failed</p>
                <p className="text-sm text-destructive mt-1">{jobResult.error_message || 'An error occurred'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credit Info */}
        <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/10" data-testid="credit-info-bar">
          <AlertCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{config.credits} credits</span> per {config.name.toLowerCase()} job.
            {user ? (
              <> Your balance: <span className={`font-medium ${(user.credit_balance || 0) >= config.credits ? 'text-primary' : 'text-destructive'}`}>{user.credit_balance || 0} credits</span>.</>
            ) : (
              <> Sign in to use this tool.</>
            )}
          </div>
        </div>

        {seoConfig && <PerfectForSection items={seoConfig.perfectFor} />}
        {seoConfig && <ToolLandingSections config={seoConfig} />}
      </main>
    </div>
  );
}

